import React, { useCallback, useEffect, useState } from 'react';
import { Cloud, Link2, Loader, CheckCircle, AlertCircle, Upload, Shield, ShieldCheck } from 'lucide-react';
import api from '../../services/api';
import NoteDocumentPreview from '../NoteDocumentPreview';

declare global {
  interface Window {
    google?: any;
    gapi?: any;
  }
}

type SelectedFile = {
  id: string;
  name: string;
  mimeType?: string;
  metadata?: any;
};

type ImportResult = {
  fileId: string;
  status: string;
  permissionStatus?: string;
  error?: string;
  metadata?: { title?: string };
};

const YEAR_LABELS: Record<string, string> = {
  hazirlik: 'Hazırlık',
  sinif1: '1. Sınıf',
  sinif2: '2. Sınıf',
  sinif3: '3. Sınıf',
  sinif4: '4. Sınıf',
  yuksek_lisans: 'Yüksek Lisans',
  doktora: 'Doktora',
  mezun: 'Mezun',
};

const SUBJECT_LABELS: Record<string, string> = {
  matematik: 'Matematik',
  fizik: 'Fizik',
  kimya: 'Kimya',
  biyoloji: 'Biyoloji',
  turkce: 'Türkçe',
  tarih: 'Tarih',
  cografya: 'Coğrafya',
  felsefe: 'Felsefe',
  edebiyat: 'Edebiyat',
  diger: 'Diğer',
};

function confidenceColor(confidence: number): string {
  if (confidence >= 0.85) return 'var(--accent-primary)';
  if (confidence >= 0.5) return '#fbbf24';
  return '#f87171';
}

const DriveImportPanel: React.FC = () => {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SelectedFile[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [pickerReady, setPickerReady] = useState(false);

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminGoogleStatus();
      setStatus(res.data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Google durumu alınamadı';
      setMessage(
        msg.includes('Access denied') || msg.includes('token') || msg.includes('401')
          ? 'Oturum bulunamadı. www.allonetoplulugu.com üzerinden çıkış yapıp tekrar giriş yapın.'
          : msg
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'pending') {
      setMessage('Giriş yaptıktan sonra Google bağlantısını tekrar deneyin.');
    }
  }, []);

  useEffect(() => {
    const clientId = status?.pickerClientId;
    if (!clientId || !status?.connected) return;

    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('picker', () => setPickerReady(true));
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [status]);

  const connectGoogle = async () => {
    try {
      sessionStorage.setItem('auth_redirect', '/admin?google=connected');
      const res = await api.adminGoogleAuthUrl();
      window.location.href = res.data.url;
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Bağlantı başlatılamadı');
    }
  };

  const openPicker = async () => {
    const clientId = status?.pickerClientId;
    const apiKey = status?.pickerApiKey;
    if (!clientId || !apiKey || !window.google?.picker) {
      setMessage('Google Picker yapılandırılmamış. Admin panelinden Google bağlantısını kontrol edin.');
      return;
    }

    let accessToken = '';
    try {
      const tokenRes = await api.adminGooglePickerToken();
      accessToken = tokenRes.data?.accessToken || '';
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Picker token alınamadı');
      return;
    }

    const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false);

    const picker = new window.google.picker.PickerBuilder()
      .setAppId(clientId.split('-')[0])
      .setOAuthToken(accessToken)
      .setDeveloperKey(apiKey)
      .setCallback((data: any) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const docs = (data.docs || []).map((d: any) => ({
            id: d.id,
            name: d.name || d.url,
            mimeType: d.mimeType,
          }));
          setSelected((prev) => {
            const map = new Map(prev.map((f) => [f.id, f]));
            docs.forEach((d: SelectedFile) => map.set(d.id, d));
            return Array.from(map.values());
          });
        }
      })
      .addView(view)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .build();

    picker.setVisible(true);
  };

  const loadPreview = async (fileId: string) => {
    setPreviewId(fileId);
    try {
      const res = await api.adminGooglePreview(fileId);
      setPreview(res.data);
    } catch {
      setPreview(null);
    }
  };

  const runImport = async () => {
    if (selected.length === 0) return;
    setImporting(true);
    setMessage(null);
    setImportResults([]);
    try {
      await api.adminGoogleSeedCatalog();
      const res = await api.adminGoogleImport(
        selected.map((f) => f.id),
        true
      );
      const results: ImportResult[] = res.data?.results || [];
      const summary = res.data?.summary;
      setImportResults(results);

      const parts: string[] = [];
      if (summary?.imported) parts.push(`${summary.imported} dosya aktarıldı`);
      if (summary?.permissionFailed) parts.push(`${summary.permissionFailed} izin hatası`);
      if (summary?.reviewRequired) parts.push(`${summary.reviewRequired} inceleme gerekli`);
      if (summary?.errors) parts.push(`${summary.errors} hata`);
      setMessage(parts.length > 0 ? parts.join(', ') + '.' : 'İşlem tamamlandı.');

      if (summary?.imported > 0) {
        setSelected([]);
        setPreview(null);
        setPreviewId(null);
      }
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Aktarım başarısız');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader size={24} className="spin" />
      </div>
    );
  }

  const meta = preview?.metadata;
  const contentExtraction = preview?.contentExtraction;
  const folderPath: string[] = preview?.folderPath || [];

  const sourceBadgeLabel =
    contentExtraction?.skipReason === 'folder_catalog_resolved'
      ? 'Klasör (ders adı)'
      : contentExtraction?.contentSource === 'pdf'
        ? 'PDF metni'
        : contentExtraction?.contentSource === 'ocr'
          ? 'OCR'
          : contentExtraction?.contentSource === 'export'
            ? 'Google export'
            : meta?.classificationSource === 'folder_catalog'
              ? 'Klasör (ders adı)'
              : meta?.classificationSource === 'folder'
                ? 'Klasör'
                : meta?.isNoiseFileName
                  ? 'Dosya adı yok sayıldı'
                  : 'Dosya adı';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(280px,360px)', gap: 20 }}>
      <div className="glow-card" style={{ padding: 24, borderRadius: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Cloud size={22} color="var(--accent-primary)" />
          <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem' }}>Google Drive Import</h3>
        </div>

        {!status?.connected ? (
          <div style={{ textAlign: 'center', padding: '24px 12px' }}>
            <p style={{ color: '#94a3b8', marginBottom: 16 }}>
              Admin Gmail hesabınızı bağlayın. Üye yüklemeleri bu hesabın Drive klasörüne gider.
            </p>
            <button type="button" onClick={connectGoogle} className="btn-shimmer" style={primaryBtn}>
              <Link2 size={16} /> Google hesabını bağla
            </button>
          </div>
        ) : (
          <>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 16px' }}>
              Bağlı: <strong style={{ color: '#e2e8f0' }}>{status.email}</strong>
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <button type="button" onClick={openPicker} disabled={!pickerReady} style={secondaryBtn}>
                Drive&apos;dan seç {pickerReady ? '' : '(Picker yükleniyor)'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  await api.adminGoogleDisconnect();
                  refreshStatus();
                }}
                style={ghostBtn}
              >
                Bağlantıyı kes
              </button>
            </div>

            {selected.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: 13 }}>Henüz dosya seçilmedi.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selected.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => loadPreview(f.id)}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: previewId === f.id ? '1px solid var(--accent-primary-glow)' : '1px solid rgba(51,65,85,0.3)',
                      background: previewId === f.id ? 'var(--accent-primary-soft)' : 'rgba(15,23,42,0.4)',
                      color: '#e2e8f0',
                      cursor: 'pointer',
                    }}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              disabled={importing || selected.length === 0}
              onClick={runImport}
              style={{ ...primaryBtn, width: '100%', marginTop: 20 }}
            >
              {importing ? <Loader size={16} className="spin" /> : <Upload size={16} />}
              {importing ? 'Aktarılıyor...' : `${selected.length} dosyayı Aktar`}
            </button>

            {importResults.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Import sonuçları</p>
                {importResults.map((r) => (
                  <div
                    key={r.fileId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: r.status === 'imported' ? 'var(--accent-primary)' : '#f87171',
                      padding: '6px 10px',
                      borderRadius: 8,
                      background: 'rgba(15,23,42,0.5)',
                    }}
                  >
                    {r.status === 'imported' ? (
                      r.permissionStatus === 'already_public' ? (
                        <ShieldCheck size={14} />
                      ) : (
                        <Shield size={14} />
                      )
                    ) : (
                      <AlertCircle size={14} />
                    )}
                    <span style={{ flex: 1, color: '#e2e8f0' }}>
                      {r.metadata?.title || r.fileId.slice(0, 8)}
                    </span>
                    <span>
                      {r.status === 'imported'
                        ? r.permissionStatus === 'already_public'
                          ? 'Herkese açık (zaten)'
                          : 'Herkese açık'
                        : r.status === 'permission_failed'
                          ? 'İzin hatası'
                          : r.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {message && (
          <p
            style={{
              marginTop: 16,
              fontSize: 13,
              color: message.includes('aktarıldı') || message.includes('tamamlandı') ? 'var(--accent-primary)' : '#f87171',
            }}
          >
            {message.includes('aktarıldı') || message.includes('tamamlandı') ? (
              <CheckCircle size={14} style={{ verticalAlign: 'middle' }} />
            ) : (
              <AlertCircle size={14} style={{ verticalAlign: 'middle' }} />
            )}{' '}
            {message}
          </p>
        )}
      </div>

      <div className="glow-card" style={{ padding: 20, borderRadius: 16 }}>
        <h4 style={{ margin: '0 0 12px', color: 'white' }}>Önizleme & Metadata</h4>
        {preview?.file ? (
          <>
            <NoteDocumentPreview
              fileUrl={`https://drive.google.com/file/d/${preview.file.id}/view`}
              fileName={preview.file.name}
              height={160}
              interactive
            />
            {meta && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: 'var(--accent-primary-soft)',
                      color: 'var(--accent-primary)',
                      border: '1px solid var(--shadow-glow)',
                    }}
                  >
                    Kaynak: {sourceBadgeLabel}
                  </span>
                  {meta.isNoiseFileName && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: 6,
                        background: 'rgba(251,191,36,0.12)',
                        color: '#fbbf24',
                        border: '1px solid rgba(251,191,36,0.25)',
                      }}
                    >
                      Gürültülü dosya adı
                    </span>
                  )}
                </div>
                {meta.isNoiseFileName && (
                  <div style={{ color: '#fbbf24', marginBottom: 8, fontSize: 11 }}>
                    Dosya adı yok sayıldı (WhatsApp vb.); sınıflandırma dosya içeriğinden yapıldı.
                  </div>
                )}
                {contentExtraction?.snippet && (
                  <div style={{ marginBottom: 10 }}>
                    <strong style={{ color: '#e2e8f0' }}>İçerik:</strong>
                    <pre
                      style={{
                        margin: '6px 0 0',
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: 'rgba(15,23,42,0.6)',
                        border: '1px solid rgba(51,65,85,0.35)',
                        color: '#cbd5e1',
                        fontSize: 11,
                        fontFamily: 'ui-monospace, monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 80,
                        overflow: 'auto',
                      }}
                    >
                      {contentExtraction.snippet}
                    </pre>
                  </div>
                )}
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Başlık:</strong>{' '}
                  <span style={{ color: 'var(--accent-primary)' }}>{meta.title}</span>
                </div>
                {meta.courseCode && (
                  <div>
                    <strong style={{ color: '#e2e8f0' }}>Ders kodu:</strong> {meta.courseCode}
                    {meta.courseNameTr ? ` (${meta.courseNameTr})` : meta.courseName ? ` (${meta.courseName})` : ''}
                  </div>
                )}
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Not türü:</strong> {meta.noteType || '—'}
                </div>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Bölüm:</strong> {meta.department || '—'}
                </div>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Sınıf:</strong>{' '}
                  {YEAR_LABELS[meta.universityYear] || meta.universityYear || '—'}
                </div>
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Konu:</strong>{' '}
                  {SUBJECT_LABELS[meta.subject] || meta.subject || '—'}
                </div>
                {folderPath.length > 0 && (
                  <div>
                    <strong style={{ color: '#e2e8f0' }}>Klasör:</strong>{' '}
                    <span style={{ wordBreak: 'break-word' }}>{folderPath.join(' / ')}</span>
                  </div>
                )}
                <div>
                  <strong style={{ color: '#e2e8f0' }}>Güven:</strong>{' '}
                  <span style={{ color: confidenceColor(meta.confidence), fontWeight: 600 }}>
                    {Math.round(meta.confidence * 100)}%
                  </span>
                  <span style={{ color: '#64748b', marginLeft: 6 }}>({meta.matchedRule})</span>
                </div>
                {meta.requiresReview && (
                  <div style={{ color: '#fbbf24', marginTop: 6 }}>
                    Düşük güven — admin incelemesi önerilir
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <p style={{ color: '#64748b', fontSize: 13 }}>Önizlemek için soldan bir dosya seçin.</p>
        )}
      </div>
    </div>
  );
};

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '12px 18px',
  borderRadius: 12,
  border: 'none',
  background: 'var(--gradient-primary)',
  color: 'white',
  fontWeight: 700,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  background: 'var(--accent-primary-soft)',
  color: 'var(--accent-primary)',
  border: '1px solid var(--accent-primary-border)',
};

const ghostBtn: React.CSSProperties = {
  ...secondaryBtn,
  background: 'transparent',
  color: '#94a3b8',
  border: '1px solid rgba(51,65,85,0.35)',
};

export default DriveImportPanel;
